import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { downloadPdf } from '@/lib/pdfDownload';

/**
 * Pill-style "Download PDF" action button.
 *
 * Props:
 *   path        string   e.g. "/exports/retirement-projection.pdf"
 *   params      object   query params
 *   filename    string   fallback filename
 *   label       string   default "Download PDF"
 *   testId      string   data-testid
 */
export const DownloadPdfButton = ({
  path,
  params = {},
  filename,
  label = 'Download PDF',
  testId = 'download-pdf-btn',
  className = '',
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    setBusy(true);
    setError('');
    try {
      await downloadPdf(path, params, filename);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid={testId}
        className={
          'inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-medium ' +
          'bg-[#1B2A4A] text-white hover:bg-[#0F1A33] active:bg-[#0A1228] ' +
          'transition-colors duration-150 ' +
          'disabled:opacity-60 disabled:cursor-not-allowed ' +
          className
        }
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        <span>{busy ? 'Generating…' : label}</span>
      </button>
      {error && (
        <span className="text-[10px] text-red-600" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
    </div>
  );
};

export default DownloadPdfButton;
